import {
  arrow,
  autoUpdate,
  computePosition,
  Coords,
  flip,
  offset,
  shift,
  size,
} from '@floating-ui/dom'
import { clsx } from 'clsx'
import { TemplateResult, unsafeCSS } from 'lit'
import { property, query } from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { SizedMixin } from '@/internal/mixin/sizedMixin'

import { FloatingStrategy, PopupPlacement } from '../../common/types'
import { DdsElement } from '../../internal/dds-hu-element'
import styles from './popup.scss?inline'

export default class PopupBaseElement extends SizedMixin(DdsElement) {
  static readonly styles = unsafeCSS(styles)

  private triggerElement: Element | null = null
  private cleanup: ReturnType<typeof autoUpdate> | undefined
  private intersectionObserver: IntersectionObserver | null = null

  @query('.popup') private readonly popup!: HTMLDivElement
  @query('.popup__arrow') private readonly arrowElement?: HTMLDivElement

  /** The disabled state of the popup. */
  @property({ reflect: true, type: Boolean }) disabled? = false

  /** The open state of the popup. */
  @property({ reflect: true, type: Boolean }) opened? = false

  /** The placement of the popup.
   * @type {'top' | 'right' | 'bottom' | 'left' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end' | 'left-start' | 'left-end' | 'right-start' | 'right-end'}
   */
  @property({ reflect: true }) placement: PopupPlacement = 'bottom'

  /** The floating strategy of the popup.
   * @type {'absolute' | 'fixed'}
   */
  @property({ reflect: true })
  floatingStrategy: FloatingStrategy = 'absolute'

  /** The offset of the popup. */
  @property({ type: Number }) offset = 0

  /** Whether the popup should sync its width with the trigger. */
  @property({ type: Boolean }) sync?: boolean

  /** The maximum height of the popup. */
  @property({ type: Number }) maxHeight: number | 'auto' = 250

  /** The maximum width of the popup. */
  @property({ type: Number }) maxWidth: number | 'auto' = 'auto'

  /** Whether the popup has an arrow. */
  @property({ type: Boolean }) hasArrow: boolean

  /** Whether the popup should overflow. */
  @property({ type: Boolean }) overflow: boolean

  /** Whether the popup should take full width of the screen. */
  @property({ type: Boolean }) fullWidth: boolean

  async connectedCallback() {
    super.connectedCallback()

    await this.updateComplete
    this.startUpdate()
  }

  protected firstUpdated(changedProps: Map<string, unknown>) {
    super.firstUpdated(changedProps)

    this.triggerElement?.setAttribute('aria-controls', 'popup')
    this.triggerElement?.setAttribute('aria-expanded', 'false')
  }

  async updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps)

    if (changedProps.has('opened')) {
      if (this.opened) {
        this.startUpdate()
        this.triggerElement?.setAttribute('aria-expanded', 'true')
        this.emit('dds-opened')
      } else {
        this.stopUpdate()
        this.triggerElement?.setAttribute('aria-expanded', 'false')
        this.emit('dds-closed')
      }
    }

    if (changedProps.has('anchor')) {
      this._handleTriggerSlotChange()
    }

    if (this.opened) {
      await this.updateComplete
      this.updatePosition()
    }
  }

  async _handleTriggerSlotChange(e?: Event | undefined) {
    this._handleTriggerSlotChangeCallback(e)

    await this.stopUpdate()

    this.triggerElement = this.querySelector<HTMLElement>('[slot="trigger"]')

    if (!this.triggerElement) {
      this.triggerElement = this.shadowRoot?.querySelector(
        'slot[name="trigger"] *',
      ) as HTMLElement
    }

    if (this.triggerElement instanceof HTMLSlotElement) {
      this.triggerElement = this.triggerElement.assignedElements({
        flatten: true,
      })[0] as HTMLElement
    }

    if (this.triggerElement) {
      this.startUpdate()
    }
  }

  public _handleTriggerSlotChangeCallback(_e?: Event) {
    return
  }

  public _handleContentSlotChange(_e: Event) {
    return
  }

  public startUpdate() {
    if (!this.triggerElement) {
      return
    }

    // Create IntersectionObserver to detect when trigger is out of viewport
    this.intersectionObserver?.disconnect()
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        // If trigger is not intersecting (out of viewport), close the popup
        if (entries[0] && !entries[0].isIntersecting && this.opened) {
          this.opened = false
          this.emit('dds-close')
        }
      },
      { threshold: 0 },
    )
    this.intersectionObserver.observe(this.triggerElement)

    this.cleanup = autoUpdate(this.triggerElement, this.popup, () => {
      this.updatePosition()
    })
  }

  public async stopUpdate(): Promise<void> {
    return new Promise(resolve => {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect()
        this.intersectionObserver = null
      }

      if (this.cleanup) {
        this.cleanup()
        this.cleanup = undefined
        requestAnimationFrame(() => resolve())
      } else {
        resolve()
      }
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    // Clean up the IntersectionObserver
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
      this.intersectionObserver = null
    }

    this.stopUpdate()
  }

  private async updatePosition() {
    if (this.disabled || !this.triggerElement) {
      return
    }

    // Check if trigger element is visible in viewport
    const triggerRect = this.triggerElement.getBoundingClientRect()
    const isVisible =
      triggerRect.top < window.innerHeight &&
      triggerRect.bottom > 0 &&
      triggerRect.left < window.innerWidth &&
      triggerRect.right > 0

    if (!isVisible) {
      if (this.opened) {
        this.opened = false
        this.emit('dds-close')
      }
      return
    }

    const { x, y, placement, middlewareData } = await computePosition(
      this.triggerElement,
      this.popup,
      {
        placement: this.placement,
        strategy: this.floatingStrategy,
        middleware: [
          offset(this.offset),
          flip(),
          shift(),
          size({
            apply: ({ rects }) => {
              const syncWidth = this.sync
              this.popup.style.width = syncWidth
                ? `${rects.reference.width}px`
                : ''
              this.popup.style.maxHeight = this.maxHeight
                ? `${this.maxHeight}px`
                : ''

              this.popup.style.maxWidth = this.maxWidth
                ? `${this.maxWidth}px`
                : ''

              if (this.fullWidth) {
                this.popup.style.width = '100vw'
              }
            },
          }),
          ...(this.arrowElement
            ? [arrow({ element: this.arrowElement, padding: 8 })]
            : []),
        ],
      },
    )

    Object.assign(this.popup.style, {
      left: `${x}px`,
      top: `${y}px`,
    })

    if (this.arrowElement) {
      const { x: arrowX, y: arrowY } = middlewareData.arrow as Coords
      const [side] = placement.split('-')
      const staticSide =
        {
          top: 'bottom',
          right: 'left',
          bottom: 'top',
          left: 'right',
        }[side] ?? ''

      Object.assign(this.arrowElement.style, {
        left: arrowX != null ? `${arrowX}px` : '',
        top: arrowY != null ? `${arrowY}px` : '',
        [staticSide]: `-5px`,
      })
    }
  }

  renderTrigger(): TemplateResult {
    return html``
  }

  renderPopup() {
    return html`
      <slot name="trigger" @slotchange=${this._handleTriggerSlotChange}>
        ${this.renderTrigger()}
      </slot>
      <div
        part="popup"
        id="popup"
        aria-hidden=${this.opened ? 'false' : 'true'}
        class=${clsx('popup', `popup--${this.size}`, {
          'popup--opened': this.opened,
          'popup--fixed': this.floatingStrategy === 'fixed',
          'popup--has-arrow': this.hasArrow,
          'popup--overflow': this.overflow,
          'popup--full-width': this.fullWidth,
        })}>
        <slot @slotchange=${this._handleContentSlotChange}></slot>
        ${this.hasArrow
          ? html`<div
              part="arrow"
              class=${clsx('popup__arrow', {
                ['popup__arrow--' + this.placement.split('-')[0]]: true,
              })}
              role="presentation"></div>`
          : ''}
      </div>
    `
  }
}
